import type { AuftragsStatus, AuftragsTyp } from '@awf/shared';
import { AUFTRAGSTYPEN, type Schritt, type TypBeschreibung } from '../auftragstypen.js';
import { LinienSymbol } from '../ui/linien-symbol.js';
import { SCHRITT_SYMBOL } from '../ui/phasenleiste.js';
import stile from './run-form-page.module.css';

function istMenschSchritt(status: AuftragsStatus): boolean {
  return status === 'wartet_auf_freigabe' || status === 'wartet_auf_entscheidung';
}

type Zeile =
  { art: 'schritt'; schritt: Schritt; optional: boolean } | { art: 'gruppe'; titel: string };

// Die Gruppenueberschrift gehoert zum Pfad auf Wunsch; die Linie durch sie ist gestrichelt.
function istOptional(z: Zeile | undefined): boolean {
  return z !== undefined && (z.art === 'gruppe' || z.optional);
}

/** Die Schritte des Typs, der Pfad auf Wunsch hinter dem Schritt eingereiht, nach dem er abzweigt. */
function zeilenVon(t: TypBeschreibung): Zeile[] {
  const zeilen: Zeile[] = [];
  for (const schritt of t.schritte) {
    zeilen.push({ art: 'schritt', schritt, optional: false });
    if (t.aufWunsch?.nach === schritt.status) {
      zeilen.push({ art: 'gruppe', titel: t.aufWunsch.titel });
      for (const s of t.aufWunsch.schritte)
        zeilen.push({ art: 'schritt', schritt: s, optional: true });
    }
  }
  return zeilen;
}

/**
 * Der Ablauf des Auftrags als ruhige Karte neben dem Formular: jede Station mit dem Symbol der
 * Schrittleiste auf der Fortschrittsseite. Frueher ein aufklappbares <details> auf beigem Grund;
 * Aufklappen brachte bei sechs Zeilen nichts (Rueckmeldung 2026-10-13). Die Marke "Ihr Schritt"
 * an Freigabe und Entscheidung ist entfallen: Vor dem Anlegen las sie sich, als stuende der
 * Auftrag schon dort (Rueckmeldung 2026-10-15).
 *
 * Die Verbindungslinie gehoert je zum Schritt, von dem sie ausgeht: So kann sie in den Pfad auf
 * Wunsch hinein und wieder heraus gestrichelt laufen, waehrend der feste Weg durchgezogen bleibt.
 */
export function AblaufSpalte({ typ }: { typ: AuftragsTyp }) {
  const t: TypBeschreibung = AUFTRAGSTYPEN[typ];
  const zeilen = zeilenVon(t);
  const mitMensch = zeilen.some((z) => z.art === 'schritt' && istMenschSchritt(z.schritt.status));
  return (
    <section className={stile.ablauf} aria-labelledby="ablauf-titel">
      <h2 id="ablauf-titel" className={stile.ablaufTitel}>
        So läuft Ihr Auftrag ab
      </h2>
      <ol className={stile.ablaufListe}>
        {zeilen.map((z, i) => {
          const naechste = zeilen[i + 1];
          const linie =
            naechste === undefined
              ? undefined
              : istOptional(z) || istOptional(naechste)
                ? 'gestrichelt'
                : 'durchgezogen';
          const vorletzte = i === zeilen.length - 2 ? 'true' : undefined;
          if (z.art === 'gruppe') {
            return (
              <li
                key={`gruppe-${i}`}
                className={stile.ablaufGruppe}
                data-linie={linie}
                data-vorletzte={vorletzte}
              >
                {z.titel}
              </li>
            );
          }
          const s = z.schritt;
          return (
            <li
              key={`${z.optional ? 'wunsch' : 'fest'}-${s.status}`}
              className={stile.ablaufSchritt}
              data-optional={z.optional ? 'true' : undefined}
              data-linie={linie}
              data-vorletzte={vorletzte}
            >
              <span className={stile.ablaufSymbol}>
                <LinienSymbol name={SCHRITT_SYMBOL[s.status] ?? 'list'} groesse={16} />
              </span>
              <span>{s.titel}</span>
            </li>
          );
        })}
      </ol>
      <p className={stile.ablaufDauer}>
        Dauer: {t.dauer}.{mitMensch && ' Sobald Sie gefragt sind, erhalten Sie eine E-Mail.'}
      </p>
    </section>
  );
}
